import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { coachService, Coach, CoachFilters, CreateCoachData, UpdateCoachData } from '../api/coachService';
import { planterService, ChurchPlanter } from '../api/planterService';

const Coaches: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [coachingMapFilter, setCoachingMapFilter] = useState('');
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState<Coach | null>(null);
  const [showPlanterModal, setShowPlanterModal] = useState(false);
  const [planterSearch, setPlanterSearch] = useState('');

  const [formData, setFormData] = useState<CreateCoachData>({
    name: '',
    email: '',
    phone: '',
    coachingMap: '',
    city: '',
    state: '',
    notes: '',
  });

  const filters: CoachFilters = {
    page,
    limit,
    search: search || undefined,
    coachingMap: coachingMapFilter || undefined,
  };

  // Fetch coaches
  const { data: coachesData, isLoading } = useQuery({
    queryKey: ['coaches', filters],
    queryFn: () => coachService.getAllCoaches(filters),
  });

  const coaches: Coach[] = coachesData?.data || [];
  const pagination = coachesData?.pagination;

  // Fetch planters for "Add from Planters" feature
  const { data: plantersData } = useQuery({
    queryKey: ['planters', { search: planterSearch, limit: 100 }],
    queryFn: () => planterService.getAllPlanters({ search: planterSearch, limit: 100 }),
    enabled: showPlanterModal,
  });

  const planters: ChurchPlanter[] = plantersData?.data || [];

  // Create coach mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCoachData) => coachService.createCoach(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaches'] });
      setShowModal(false);
      resetForm();
    },
  });

  // Update coach mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCoachData }) =>
      coachService.updateCoach(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaches'] });
      setShowModal(false);
      setSelectedCoach(null);
      resetForm();
    },
  });

  // Delete coach mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => coachService.deleteCoach(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaches'] });
      setShowDeleteConfirm(false);
      setCoachToDelete(null);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      coachingMap: '',
      city: '',
      state: '',
      notes: '',
    });
  };

  const handleEdit = (coach: Coach) => {
    setSelectedCoach(coach);
    setFormData({
      name: coach.name,
      email: coach.email || '',
      phone: coach.phone || '',
      coachingMap: coach.coachingMap || '',
      city: coach.city || '',
      state: coach.state || '',
      notes: coach.notes || '',
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedCoach(null);
    resetForm();
    setShowModal(true);
  };

  const handleAddFromPlanters = () => {
    setPlanterSearch('');
    setShowPlanterModal(true);
  };

  const handleSelectPlanter = (planter: ChurchPlanter) => {
    setFormData({
      name: planter.name,
      email: planter.email || planter.user?.email || '',
      phone: planter.phone || '',
      coachingMap: '', // Leave empty - needs to be filled in
      city: planter.city || '',
      state: planter.state || '',
      notes: `Added from Church Planter: ${planter.name}`,
    });
    setShowPlanterModal(false);
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCoach) {
      updateMutation.mutate({ id: selectedCoach.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteClick = (coach: Coach) => {
    setCoachToDelete(coach);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (coachToDelete) {
      deleteMutation.mutate(coachToDelete.id);
    }
  };

  const uniqueCoachingMaps = Array.from(new Set(coaches.map(c => c.coachingMap).filter(Boolean)));

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Coaches</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage coach directory and coaching event information
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddFromPlanters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Add from Planters
              </button>
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Coach
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <input
                  type="text"
                  placeholder="Search coaches..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <select
                  value={coachingMapFilter}
                  onChange={(e) => setCoachingMapFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Coaching Maps</option>
                  {uniqueCoachingMaps.map((map) => (
                    <option key={map} value={map}>{map}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Coaches Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="px-4 py-12 text-center text-gray-500">Loading coaches...</div>
          ) : coaches.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              No coaches found. Click "Add Coach" to create one.
            </div>
          ) : (
            <ResponsiveTableWrapper>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Coaching Map
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coaches.map((coach) => (
                  <tr key={coach.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{coach.name}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coach.email}</div>
                      <div className="text-sm text-gray-500">{coach.phone}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coach.coachingMap || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coach.city && coach.state ? `${coach.city}, ${coach.state}` : coach.city || coach.state || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                      <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end sm:items-center">
                        <button
                          onClick={() => handleEdit(coach)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(coach)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ResponsiveTableWrapper>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{page}</span> of{' '}
                  <span className="font-medium">{pagination.pages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)} />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {selectedCoach ? 'Edit Coach' : 'Add Coach'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Coaching Map</label>
                        <input
                          type="text"
                          value={formData.coachingMap}
                          onChange={(e) => setFormData({ ...formData, coachingMap: e.target.value })}
                          placeholder="e.g., Feb 2025"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">City</label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">State</label>
                          <input
                            type="text"
                            value={formData.state}
                            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          rows={3}
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setSelectedCoach(null);
                        resetForm();
                      }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Select Planter Modal */}
        {showPlanterModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPlanterModal(false)} />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Select Church Planter</h3>
                    <button
                      onClick={() => setShowPlanterModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search planters..."
                      value={planterSearch}
                      onChange={(e) => setPlanterSearch(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {planters.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {planterSearch ? 'No planters found' : 'Start typing to search planters'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {planters.map((planter) => (
                          <button
                            key={planter.id}
                            onClick={() => handleSelectPlanter(planter)}
                            className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{planter.name}</div>
                                <div className="text-sm text-gray-500">{planter.email || planter.user?.email}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500">{planter.phone}</div>
                                <div className="text-sm text-gray-500">
                                  {planter.city && planter.state ? `${planter.city}, ${planter.state}` : planter.city || planter.state}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6">
                  <button
                    onClick={() => setShowPlanterModal(false)}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && coachToDelete && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteConfirm(false)} />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Coach</h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete <strong>{coachToDelete.name}</strong>? This action cannot be undone.
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleteMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setCoachToDelete(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Coaches;

